/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import input from "../../../../assets/styles/components/input.module.css";
import select from "../../../../assets/styles/components/select.module.css";
import {
  fetchFinancialEntryByUid,
  fetchPaymentTypes,
  registerOrEditFinancialEntry,
} from "../../../../services/financial";
import { loadAllSuppliers } from "../../../../services/suppliers";
import type { IFinancialEntry } from "../../../../types/financial";
import Button from "../../../../components/Button/Button";
import SelectChartOfAccounts from "../CashFlow/SelectChartOfAccounts";
import SelectCostCenter from "../CashFlow/SelectCostCenter";
import SelectFinanceAccount from "../CashFlow/SelectFinanceAccount";
import InputSuspense from "../../../../components/InputSuspense/InputSuspense";
import styles from "./ExpensesAdd.module.css";

// Interfaces locais
interface FornecedorSelect {
  uid_fornecedor: string;
  nome_fantasia: string;
  razao_social?: string;
  nome?: string;
}

interface PaymentTypeSelect {
  uid_tipo_pagamento: string;
  titulo: string;
  ordem?: number;
}

type Tipo = "pagar" | "receber";
type Origem = "pedido" | "venda" | "compra" | "manual" | "mensalidade" | "rh";
type Status = "aberto" | "pago" | "atrasado" | "cancelado";

export interface FinancialEntryFormData {
  uid_financeiro_lancamento?: string;
  tipo: Tipo;
  numero_documento: string | null;
  descricao: string;
  codigo_estabelecimento?: number;
  uid_cliente?: string | null;
  uid_fornecedor?: string | null;
  uid_colaborador?: string | null;
  uid_plano_contas: string | null;
  uid_centro_custo?: string | null;
  uid_conta_financeira?: string | null;
  origem: Origem;
  valor: number;
  desconto: number;
  juros: number;
  valor_total: number;
  valor_parcela: number;
  parcela: number;
  total_parcelas: number;
  data_emissao: string | null;
  data_competencia?: string | null;
  data_vencimento: string | null;
  data_pagamento?: string | null;
  status: Status;
  uid_tipo_pagamento?: string | null;
  observacao?: string | null;
}

interface FormExpensesProps {
  uid?: string;
  isEdit: boolean;
  handleVisible: (key: string, value: boolean) => void;
  onSuccessSave: (newUid: string) => void;
  entryToEdit?: IFinancialEntry;
  entryToCopy?: IFinancialEntry;
}

const FormExpenses = ({
  uid,
  isEdit,
  handleVisible,
  onSuccessSave,
  entryToEdit,
  entryToCopy,
}: FormExpensesProps) => {
  const queryClient = useQueryClient();

  const location = useLocation();

  const stateSuppliers = location.state?.from_suppliers;

  // Estados locais do formulário
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [isActiveSupplier, setIsActiveSupplier] = useState(false);
  const [selectedSupplierName, setSelectedSupplierName] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [originalValue, setOriginalValue] =
    useState<FinancialEntryFormData | null>(null);

  // Estados de cálculo
  const [valorBruto, setValorBruto] = useState(0);
  const [valorDesconto, setValorDesconto] = useState(0);
  const [valorJuros, setValorJuros] = useState(0);
  const [valorTotalCalculado, setValorTotalCalculado] = useState(0);

  useEffect(() => {
    if (stateSuppliers) {
      const {
        descricao,
        tipo_pagamento,
        uid_fornecedor,
        origem,
        valor,
        emissao,
        status,
        fornecedor_nome,
      } = stateSuppliers;

      setValue("descricao", descricao);
      setValue("uid_tipo_pagamento", tipo_pagamento);
      setSelectedSupplierName(fornecedor_nome);
      setValue("uid_fornecedor", uid_fornecedor);
      setValue("origem", origem);
      setValue("valor_total", valor);
      setValorBruto(valor);
      setValue("data_emissao", emissao);
      setValue("status", status);
    }
  }, [stateSuppliers]);

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
    getValues,
  } = useForm<FinancialEntryFormData>({
    defaultValues: {
      tipo: "pagar",
      origem: "manual",
      status: "aberto",
      parcela: 1,
      total_parcelas: 1,
      valor: 0,
      desconto: 0,
      juros: 0,
      valor_total: 0,
      valor_parcela: 0,
      uid_cliente: null,
      uid_fornecedor: null,
      uid_colaborador: null,
      uid_tipo_pagamento: "",
      data_pagamento: null,
      observacao: null,
      uid_plano_contas: null,
      uid_centro_custo: "",
      uid_conta_financeira: null,
      codigo_estabelecimento: undefined,
      data_emissao: new Date().toISOString().split("T")[0],
      data_competencia: new Date().toISOString().split("T")[0],
      data_vencimento: null,
    },
  });

  const totalParcelas = watch("total_parcelas");
  const uidTipoPagamentoWatch = watch("uid_tipo_pagamento");
  const uidPlanoContasWatch = watch("uid_plano_contas");
  const uidCentroCustoWatch = watch("uid_centro_custo");
  const uidContaFinanceiraWatch = watch("uid_conta_financeira");
  const watchedStatus = watch("status");
  const watchedVencimento = watch("data_vencimento");

  // Queries
  const { data: fetchedEntry, isLoading: isLoadingEntry } = useQuery({
    queryKey: ["financialEntry", uid],
    queryFn: () =>
      fetchFinancialEntryByUid({
        uid: uid!,
        type: "pagar",
      }),
    enabled: !!uid && !entryToEdit && !entryToCopy,
  });

  const { data: suppliersData, isLoading: isLoadingSuppliers } = useQuery({
    queryKey: ["allSuppliersForSelect", supplierSearchTerm],
    queryFn: () => loadAllSuppliers(supplierSearchTerm, 1),
  });

  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = useQuery<
    PaymentTypeSelect[],
    Error
  >({
    queryKey: ["paymentTypesForSelect"],
    queryFn: fetchPaymentTypes,
  });

  const suppliersForSelect: FornecedorSelect[] = useMemo(() => {
    return suppliersData?.data?.suppliers || [];
  }, [suppliersData]);

  const paymentTypesList: PaymentTypeSelect[] = paymentTypesData || [];

  // Mutation
  const { mutateAsync, isPending } = useMutation({
    mutationFn: registerOrEditFinancialEntry,
    onSuccess: (response: any) => {
      toast.success(response.message);

      if (response?.data?.uid_financeiro_lancamento) {
        onSuccessSave(response.data.uid_financeiro_lancamento);
      }

      queryClient.invalidateQueries({ queryKey: ["financialEntries"] });
      queryClient.invalidateQueries({ queryKey: ["financialEntry", uid] });

      handleVisible("isEdit", false);
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  // Efeitos e Lógica de Negócio
  useEffect(() => {
    register("uid_plano_contas");
    register("uid_centro_custo");
    register("uid_conta_financeira");
    register("uid_fornecedor");
    register("uid_colaborador");
    register("valor", { required: "O valor é obrigatório", min: { value: 0.01, message: "O valor deve ser maior que zero" } });
  }, [register]);

  useEffect(() => {
    const total = valorBruto - valorDesconto + valorJuros;
    const finalTotal = total > 0 ? total : 0;
    setValorTotalCalculado(finalTotal);
    setValue("valor_total", finalTotal);
  }, [valorBruto, valorDesconto, valorJuros, setValue]);

  useEffect(() => {
    const numParcelas = totalParcelas || 1;
    const calcValorParcela =
      numParcelas > 0 ? valorTotalCalculado / numParcelas : valorTotalCalculado;
    setValue("valor_parcela", calcValorParcela);
  }, [valorTotalCalculado, totalParcelas, setValue]);

  // const resetForm = useCallback(() => {
  //   reset({
  //     tipo: "pagar",
  //     origem: "manual",
  //     status: "aberto",
  //     parcela: 1,
  //     total_parcelas: 1,
  //     valor: 0,
  //     desconto: 0,
  //     juros: 0,
  //     valor_total: 0,
  //     valor_parcela: 0,
  //     uid_cliente: null,
  //     uid_fornecedor: null,
  //     uid_colaborador: null,
  //     uid_tipo_pagamento: "",
  //     observacao: null,
  //     data_emissao: new Date().toISOString().split("T")[0],
  //     data_competencia: new Date().toISOString().split("T")[0],
  //     data_vencimento: null,
  //     data_pagamento: null,
  //     uid_plano_contas: null,
  //     uid_centro_custo: "",
  //     uid_conta_financeira: null,
  //     codigo_estabelecimento: undefined,
  //   });

  //   setValorBruto(0);
  //   setValorDesconto(0);
  //   setValorJuros(0);
  //   setSelectedSupplierName("");
  // }, [reset]);

  // Inicialização do Formulário
  useEffect(() => {
    const dataToUse = entryToEdit || fetchedEntry;

    if (dataToUse && !isInitialized) {
      const valuesToReset = Object.entries(dataToUse).reduce(
        (acc, [key, value]) => {
          let formattedValue: any = value;

          if (
            [
              "data_emissao",
              "data_vencimento",
              "data_pagamento",
              "data_competencia",
            ].includes(key)
          ) {
            formattedValue = value ? String(value).split("T")[0] : null;
          }

          if (
            [
              "valor",
              "valor_total",
              "desconto",
              "juros",
              "valor_parcela",
            ].includes(key)
          ) {
            formattedValue = value ? Number(value) : 0;
          }

          // @ts-ignore
          acc[key] = formattedValue;
          return acc;
        },
        {} as FinancialEntryFormData,
      );

      valuesToReset.tipo = "pagar";
      setValorBruto(Number(dataToUse.valor || dataToUse.valor_total || 0));
      setValorDesconto(Number(dataToUse.desconto || 0));
      setValorJuros(Number(dataToUse.juros || 0));

      if (dataToUse.uid_fornecedor) {
        const supplierName =
          dataToUse.fornecedor?.nome_fantasia || "Fornecedor selecionado";
        setSelectedSupplierName(supplierName);
      } else if (dataToUse.uid_colaborador) {
        const collaboratorName =
          dataToUse.colaborador?.nome || "Colaborador selecionado";
        setSelectedSupplierName(collaboratorName);
      }

      reset(valuesToReset);

      const paymentUid =
        dataToUse.tipo_pagamento?.uid_tipo_pagamento ??
        dataToUse.uid_tipo_pagamento ??
        "";
      setValue("uid_tipo_pagamento", paymentUid);

      setIsInitialized(true);
    } else if (entryToCopy && !isInitialized) {
      // Caso de cópia
      const valuesToReset = Object.entries(entryToCopy).reduce(
        (acc, [key, value]) => {
          let formattedValue: any = value;
          if (
            [
              "data_emissao",
              "data_vencimento",
              "data_pagamento",
              "data_competencia",
            ].includes(key)
          ) {
            formattedValue = value ? String(value).split("T")[0] : null;
          }
          if (
            [
              "valor",
              "valor_total",
              "desconto",
              "juros",
              "valor_parcela",
            ].includes(key)
          ) {
            formattedValue = value ? Number(value) : 0;
          }
          // @ts-ignore
          acc[key] = formattedValue;
          return acc;
        },
        {} as FinancialEntryFormData,
      );

      valuesToReset.tipo = "pagar";
      setValorBruto(Number(entryToCopy.valor || entryToCopy.valor_total || 0));

      if (entryToCopy.uid_fornecedor) {
        const supplierFound = suppliersForSelect.find(
          (s) => s.uid_fornecedor === entryToCopy.uid_fornecedor,
        );
        setSelectedSupplierName(
          supplierFound?.nome_fantasia ||
            entryToCopy.fornecedor?.nome_fantasia ||
            "Fornecedor selecionado",
        );
      }

      reset(valuesToReset);

      const paymentUid =
        entryToCopy.tipo_pagamento?.uid_tipo_pagamento ??
        entryToCopy.uid_tipo_pagamento ??
        "";
      setValue("uid_tipo_pagamento", paymentUid);

      setIsInitialized(true);
    } else if (!isLoadingEntry && !uid && !entryToCopy && !isInitialized) {
      // resetForm();
      setIsInitialized(true);
    }
  }, [
    entryToEdit,
    fetchedEntry,
    entryToCopy,
    isInitialized,
    reset,
    setValue,
    suppliersForSelect,
    isLoadingEntry,
    uid,
    // resetForm,
  ]);

  // Handlers
  const handleSelectPessoa = useCallback(
    (nome: string, uid: string) => {
      setValue("uid_fornecedor", uid, { shouldValidate: true });
      setValue("uid_colaborador", null);
      setSelectedSupplierName(nome);
      setSupplierSearchTerm("");
      setIsActiveSupplier(false);
    },
    [setValue],
  );

  const handleSupplierInput = (value: string) => {
    setSelectedSupplierName(value);
    setSupplierSearchTerm(value);
    setValue("uid_fornecedor", null, { shouldValidate: false });
    setIsActiveSupplier(value.length > 0);
  };

  const pessoaOptionsMemo = useMemo(() => {
    if (isLoadingSuppliers)
      return <p className={styles.dropDownLoading}>Carregando...</p>;

    if (!suppliersForSelect.length)
      return (
        <p className={styles.dropDownNoResults}>
          Nenhum fornecedor encontrado.
        </p>
      );

    return suppliersForSelect.map((item) => (
      <p
        key={item.uid_fornecedor}
        className={styles.dropDownItem}
        onClick={() =>
          handleSelectPessoa(
            item.nome_fantasia || item.nome || "Sem Nome",
            item.uid_fornecedor,
          )
        }
      >
        {item.nome_fantasia || item.nome || "Sem Nome"}
      </p>
    ));
  }, [
    suppliersForSelect,
    isLoadingSuppliers,
    handleSelectPessoa,
  ]);

  const onSubmit: SubmitHandler<FinancialEntryFormData> = async (data) => {
    try {
      const finalStatus = data.status;

      const formattedData = {
        ...data,
        tipo: "pagar" as Tipo,
        origem: "manual" as Origem,
        numero_documento: data.numero_documento,
        valor: valorBruto,
        desconto: valorDesconto,
        juros: valorJuros,
        valor_total: valorTotalCalculado,
        valor_parcela: data.valor_parcela,
        parcela: data.parcela,
        total_parcelas: data.total_parcelas,
        data_emissao: data.data_emissao ? data.data_emissao : null,
        data_competencia: data.data_competencia ? data.data_competencia : null,
        data_vencimento: data.data_vencimento ? data.data_vencimento : null,
        data_pagamento: data.data_pagamento ? data.data_pagamento : null,
        uid_cliente: null,
        uid_fornecedor: data.uid_fornecedor || null,
        uid_colaborador: null,
        uid_tipo_pagamento: data.uid_tipo_pagamento || null,
        uid_centro_custo: data.uid_centro_custo || null,
        observacao: data.observacao || null,
        codigo_estabelecimento: isEdit ? data.codigo_estabelecimento : 0,
        status: finalStatus,
      };

      if (uid) {
        formattedData.uid_financeiro_lancamento = uid;
      }

      // @ts-ignore
      await mutateAsync(formattedData);
    } catch (error) {
      console.error("Erro no formulário:", error);
    }
  };

  // ATENÇÃO: Não pague esse código, ele está desativado temporáriamente
  const handleBlurVencimento = (e: React.FocusEvent<HTMLInputElement>) => {
    const dataDigitada = e.target.value;

    if (!uid && dataDigitada) {
      // const selected = new Date(dataDigitada + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      /* if (selected.getTime() < today.getTime()) {
        const confirmar = window.confirm(
          "A data de vencimento é retroativa. Deseja lançar esse título como pago?"
        );

        if (confirmar) {
          setValue("status", "pago");
          setValue("data_pagamento", dataDigitada);
        } else {
          setValue("status", "aberto");
          setValue("data_pagamento", null);
        }
      } */
    }
  };

  useEffect(() => {
    if (isEdit) {
      setOriginalValue(getValues());
    }
  }, [isEdit]);

  // ── helpers de formatação ────────────────────────────────────────────────
  const formatBRL = (v: number) =>
    v > 0 ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";
  const formatDateDisplay = (d: string | null | undefined) => {
    if (!d) return "—";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  // ── sub-componentes de campo ─────────────────────────────────────────────
  const labelStyle: React.CSSProperties = {
    fontSize: "0.72rem", color: "#888", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block",
  };
  const labelEditStyle: React.CSSProperties = {
    ...labelStyle, color: "#00b37e",
  };
  const labelRequiredStyle: React.CSSProperties = {
    ...labelEditStyle,
  };
  const inputEditStyle: React.CSSProperties = {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1px solid #00b37e", outline: "none", fontSize: "0.9rem",
    background: "#f0fff8", boxSizing: "border-box" as const,
  };
  const inputErrorStyle: React.CSSProperties = {
    ...inputEditStyle,
    border: "1px solid #e74c3c", background: "#fff5f5",
  };
  const inputDisabledStyle: React.CSSProperties = {
    ...inputEditStyle,
    background: "#f0f0f0", color: "#888", cursor: "not-allowed",
  };
  const valueStyle: React.CSSProperties = {
    fontWeight: 500, fontSize: "0.95rem", color: "#222",
  };
  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "20px 24px",
    padding: "16px 4px",
  };
  const errorStyle: React.CSSProperties = {
    color: "#e74c3c", fontSize: "0.78rem", marginTop: 3, display: "block",
  };

  const ViewField = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <span style={labelStyle}>{label}</span>
      <div style={valueStyle}>{value || "—"}</div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>

      {/* ── Seção 1: Identificação ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div style={gridStyle}>

            {/* Descrição */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={errors?.descricao ? { ...labelStyle, color: "#e74c3c" } : labelRequiredStyle}>
                Descrição *
              </label>
              <input
                style={errors?.descricao ? inputErrorStyle : inputEditStyle}
                type="text"
                {...register("descricao", { required: "A descrição é obrigatória" })}
                placeholder="Descrição da despesa"
              />
              {errors?.descricao && <span style={errorStyle}>⚠️ {errors?.descricao.message}</span>}
            </div>

            {/* Fornecedor */}
            <div style={{ gridColumn: "span 2" }}>
              <InputSuspense
                label="Fornecedor"
                placeholder="Pesquisar..."
                value={selectedSupplierName}
                onChange={(e) => handleSupplierInput(e.target.value)}
                isActiveDropDown={isActiveSupplier}
                setIsActiveDropDown={setIsActiveSupplier}
                option={pessoaOptionsMemo}
                name="pessoa_input"
                readOnly={false}
              />
            </div>

            {/* Nº Documento */}
            <div>
              <label style={labelEditStyle}>Número do documento</label>
              <input
                style={inputEditStyle}
                type="text"
                {...register("numero_documento")}
                placeholder="Campo livre"
              />
            </div>

            {/* Tipo de Pagamento */}
            <div>
              <label style={errors?.uid_tipo_pagamento ? { ...labelStyle, color: "#e74c3c" } : labelRequiredStyle}>
                Tipo de Pagamento *
              </label>
              <select
                style={errors?.uid_tipo_pagamento ? inputErrorStyle : inputEditStyle}
                {...register("uid_tipo_pagamento", { required: "Tipo de pagamento é obrigatório" })}
                value={uidTipoPagamentoWatch || ""}
                onChange={(e) => setValue("uid_tipo_pagamento", e.target.value, { shouldValidate: true })}
                disabled={isLoadingPaymentTypes}
              >
                <option value="">Selecione</option>
                {paymentTypesList.map((tp) => (
                  <option key={tp.uid_tipo_pagamento} value={tp.uid_tipo_pagamento}>
                    {tp.titulo}
                  </option>
                ))}
              </select>
              {errors?.uid_tipo_pagamento && <span style={errorStyle}>⚠️ {errors?.uid_tipo_pagamento.message}</span>}
            </div>
          </div>
        ) : (
          <div style={gridStyle}>
            <div style={{ gridColumn: "1 / -1" }}>
              <ViewField label="Descrição" value={watch("descricao")} />
            </div>
            <ViewField label="Fornecedor" value={selectedSupplierName || "—"} />
            <ViewField label="Número do documento" value={watch("numero_documento") || "—"} />
            <ViewField
              label="Tipo de Pagamento"
              value={paymentTypesList.find(p => p.uid_tipo_pagamento === uidTipoPagamentoWatch)?.titulo || "—"}
            />
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: "1px solid #eee", margin: "0 0 20px" }} />

      {/* ── Seção 2: Classificação ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={gridStyle}>
          <div>
            <SelectFinanceAccount
              value={uidContaFinanceiraWatch || ""}
              handleChangeAccount={(value) => setValue("uid_conta_financeira", value, { shouldValidate: true, shouldDirty: true })}
              disabled={!isEdit}
            />
          </div>
          <div>
            <SelectChartOfAccounts
              value={uidPlanoContasWatch}
              handleChangeChartOfAccount={(value) => setValue("uid_plano_contas", value, { shouldValidate: true, shouldDirty: true })}
              type="despesa"
              disabled={!isEdit}
            />
          </div>
          <div>
            <SelectCostCenter
              value={uidCentroCustoWatch || ""}
              handleChangeCostCenter={(value) => setValue("uid_centro_custo", value, { shouldValidate: true, shouldDirty: true })}
              disabled={!isEdit}
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: "1px solid #eee", margin: "0 0 20px" }} />

      {/* ── Seção 3: Valores ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div style={gridStyle}>
            <div>
              <label style={errors?.descricao ? { ...labelStyle, color: "#e74c3c" } : labelRequiredStyle}>Valor *</label>
              <NumericFormat
                className={input.input}
                style={errors?.valor ? { borderColor: "#e74c3c", background: "#fff5f5" } : { borderColor: "#00b37e", background: "#f0fff8" }}
                thousandSeparator="." decimalSeparator="," prefix="R$ "
                decimalScale={2} fixedDecimalScale
                value={valorBruto}
                onFocus={(e) => e.target.select()}
                onValueChange={(v) => { setValorBruto(v.floatValue ?? 0); setValue("valor", v.floatValue ?? 0, { shouldValidate: true }); }}
                placeholder="R$ 0,00"
              />
              {errors?.valor && <span style={errorStyle}>⚠️ {errors?.valor.message}</span>}
            </div>
            <div>
              <label style={labelEditStyle}>Desconto</label>
              <NumericFormat
                className={input.input}
                style={{ borderColor: "#00b37e", background: "#f0fff8" }}
                thousandSeparator="." decimalSeparator="," prefix="R$ "
                decimalScale={2} fixedDecimalScale
                value={valorDesconto}
                onFocus={(e) => e.target.select()}
                onValueChange={(v) => { setValorDesconto(v.floatValue ?? 0); setValue("desconto", v.floatValue ?? 0); }}
                placeholder="R$ 0,00"
              />
            </div>
            <div>
              <label style={labelEditStyle}>Juros</label>
              <NumericFormat
                className={input.input}
                style={{ borderColor: "#00b37e", background: "#f0fff8" }}
                thousandSeparator="." decimalSeparator="," prefix="R$ "
                decimalScale={2} fixedDecimalScale
                value={valorJuros}
                onFocus={(e) => e.target.select()}
                onValueChange={(v) => { setValorJuros(v.floatValue ?? 0); setValue("juros", v.floatValue ?? 0); }}
                placeholder="R$ 0,00"
              />
            </div>
            <div>
              <label style={labelStyle}>Total Líquido</label>
              <NumericFormat
                className={input.input}
                style={{ background: "#f0f0f0", color: "#888", cursor: "not-allowed", fontWeight: 700 }}
                thousandSeparator="." decimalSeparator="," prefix="R$ "
                decimalScale={2} fixedDecimalScale
                value={valorTotalCalculado}
                readOnly
              />
            </div>
          </div>
        ) : (
          <div style={gridStyle}>
            <ViewField label="Valor" value={formatBRL(valorBruto)} />
            <ViewField label="Desconto" value={valorDesconto > 0 ? formatBRL(valorDesconto) : "—"} />
            <ViewField label="Juros" value={valorJuros > 0 ? formatBRL(valorJuros) : "—"} />
            <ViewField label="Total Líquido" value={<strong>{formatBRL(valorTotalCalculado)}</strong>} />
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: "1px solid #eee", margin: "0 0 20px" }} />

      {/* ── Seção 4: Datas ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div style={gridStyle}>
            <div>
              <label style={labelRequiredStyle}>Emissão *</label>
              <input
                style={inputEditStyle}
                type="date"
                {...register("data_emissao", { required: true })}
              />
            </div>
            <div>
              <label style={errors?.data_vencimento ? { ...labelStyle, color: "#e74c3c" } : labelRequiredStyle}>
                Vencimento *
              </label>
              <input
                style={errors?.data_vencimento ? inputErrorStyle : inputEditStyle}
                type="date"
                {...register("data_vencimento", {
                  required: "Data de vencimento é obrigatória",
                  onBlur: handleBlurVencimento,
                })}
              />
              {errors?.data_vencimento && <span style={errorStyle}>⚠️ {errors?.data_vencimento.message}</span>}
            </div>
            <div>
              <label style={watchedStatus !== "pago" ? { ...labelStyle, opacity: 0.5 } : labelEditStyle}>Pagamento</label>
              <input
                style={watchedStatus !== "pago" ? inputDisabledStyle : inputEditStyle}
                type="date"
                {...register("data_pagamento")}
                disabled={watchedStatus !== "pago"}
              />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <div style={{ ...inputDisabledStyle, display: "flex", alignItems: "center" }}>
                {watch("status") === "aberto" ? "Aberto" :
                  watch("status") === "pago" ? "Pago" :
                  watch("status") === "atrasado" ? "Atrasado" : "Cancelado"}
              </div>
            </div>
          </div>
        ) : (
          <div style={gridStyle}>
            <ViewField label="Emissão" value={formatDateDisplay(watch("data_emissao"))} />
            <ViewField label="Vencimento" value={formatDateDisplay(watch("data_vencimento"))} />
            <ViewField label="Pagamento" value={formatDateDisplay(watch("data_pagamento"))} />
            <ViewField label="Status" value={
              <span style={{
                padding: "3px 12px", borderRadius: 20, fontSize: "0.82rem", fontWeight: 700,
                background: watch("status") === "pago" ? "#d4edda" : watch("status") === "atrasado" ? "#f8d7da" : watch("status") === "cancelado" ? "#e2e3e5" : "#cfe2ff",
                color: watch("status") === "pago" ? "#155724" : watch("status") === "atrasado" ? "#b65f68" : watch("status") === "cancelado" ? "#495057" : "#084298",
              }}>
                {watch("status") === "aberto" ? "Aberto" : watch("status") === "pago" ? "Pago" : watch("status") === "atrasado" ? "Atrasado" : "Cancelado"}
              </span>
            } />
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ borderTop: "1px solid #eee", margin: "0 0 20px" }} />

      {/* ── Seção 5: Observação ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div>
            <label style={labelEditStyle}>Observação</label>
            <textarea
              style={{ ...inputEditStyle, minHeight: 80, resize: "vertical" }}
              {...register("observacao")}
              placeholder="Informações adicionais..."
            />
          </div>
        ) : (
          <ViewField label="Observação" value={watch("observacao") || "Nenhuma observação."} />
        )}
      </div>

      {/* ── Botões ──────────────────────────────────────────────────────────── */}
      {isEdit && (
        <div className={styles.buttonSave}>
          <Button variant="contain" type="submit" disabled={isPending || !isEdit}>
            {isPending ? "Salvando..." : "Salvar"}
          </Button>
          {uid && (
            <Button
              variant="contain"
              title="Cancelar edição"
              onClick={() => {
                handleVisible("isEdit", false);
                if (originalValue) {
                  reset(originalValue);
                  setValorBruto(originalValue.valor || 0);
                  setValorDesconto(originalValue.desconto || 0);
                  setValorJuros(originalValue.juros || 0);
                }
              }}
              typeStyle="cancel"
            >
              Cancelar
            </Button>
          )}
        </div>
      )}
    </form>
  );
};

export default FormExpenses;